import React, { Fragment, useState, useEffect } from "react";
import { client, getSanityImageUrl } from "@/lib/sanity";

// next link
import Link from "next/link";

// next image
import Image from "next/image";

// components
import CustomButton from "../CustomButton";
import PortableText from "../PortableText";

// swiper
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Thumbs } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';

// Redux Selector / Action
import { useSelector } from "react-redux";
import { theme_scheme_direction } from "../../store/setting/selectors";

// Define types based on Sanity schema
interface VideoThumbnail {
  asset: {
    _ref: string;
  };
  alt?: string;
}

interface VideoSlug {
  current: string;
}

interface VideoCategory {
  _id: string;
  title: string;
}

interface VideoItem {
  _id: string;
  title: string;
  description?: string;
  thumbnailPortrait?: VideoThumbnail;
  thumbnailLandscape?: VideoThumbnail;
  slug: VideoSlug;
  duration?: string;
  rating?: string;
  categories?: VideoCategory[];
  cast?: string[];
  ageRating?: string;
}

const OttHeroSlider = () => {
  const themeSchemeDirection = useSelector(theme_scheme_direction);
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);
  const [render, setRender] = useState(true);
  const [featuredVideos, setFeaturedVideos] = useState<VideoItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // GROQ query to fetch videos (using the same query as VideoFeed but limiting to 3)
    const query = `
      *[_type == "video"] | order(coalesce(metadata.featuredPriority, 0) desc, _createdAt desc) [0...3] {
        _id,
        title,
        description,
        thumbnailPortrait,
        thumbnailLandscape,
        slug,
        duration,
        rating,
        "categories": categories[]->{_id, title},
        "cast": cast[]->name,
        ageRating
      }
    `;

    // Fetch data from Sanity
    const fetchFeaturedVideos = async () => {
      try {
        const data = await client.fetch(query);
        console.log("Fetched featured videos:", data); // Debug log
        setFeaturedVideos(data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching featured videos:", error);
        setLoading(false);
        // Fallback to empty array
        setFeaturedVideos([]);
      }
    };

    fetchFeaturedVideos();

    // Handle render state for swiper
    setRender(false);
    setTimeout(() => {
      setRender(true);
    }, 100);

    return () => { };
  }, []);

  // Loading state
  if (loading) {
    return <div className="loading">Loading featured videos...</div>;
  }

  // If no videos were found, show an error message
  const videos = featuredVideos;

  if (!loading && (!videos || videos.length === 0)) {
    return <div className="error">No featured videos found. Please check your data source.</div>;
  }

  return (
    <Fragment>
      <div className="iq-banner-thumb-slider">
        <div className="slider">
          <div className="position-relative slider-bg d-flex justify-content-end">
            <div className="position-relative my-auto">
              <div
                className="horizontal_thumb_slider"
                data-swiper="slider-thumbs-ott"
              >
                <div className="banner-thumb-slider-nav">
                  <Swiper
                    key={String(themeSchemeDirection)}
                    dir={String(themeSchemeDirection)}
                    tag="ul"
                    thumbs={{ swiper: thumbsSwiper }}
                    direction="horizontal"
                    navigation={{
                      prevEl: ".slider-prev",
                      nextEl: ".slider-next",
                    }}
                    spaceBetween={24}
                    loop={true}
                    slidesPerView={2}
                    breakpoints={{
                      0: {
                        direction: "horizontal",
                        slidesPerView: 1,
                      },
                      768: {
                        direction: "horizontal",
                        slidesPerView: 2,
                      },
                    }}
                    watchSlidesProgress={true}
                    modules={[Navigation, Thumbs]}
                    className="swiper-horizontal swiper-container mb-0"
                  >
                    {videos.map((video, index) => (
                      <SwiperSlide className="swiper-bg" key={video._id}>
                        <div className="block-images position-relative">
                          <div className="img-box">
                            <Image
                              src={
                                video.thumbnailPortrait?.asset?._ref
                                  ? getSanityImageUrl(video.thumbnailPortrait.asset._ref, 522, 760)
                                  : video.thumbnailLandscape?.asset?._ref
                                    ? getSanityImageUrl(video.thumbnailLandscape.asset._ref, 1920, 1080)
                                    : `/assets/images/movies/ott${index + 1}.webp`
                              }
                              className="img-fluid"
                              alt={video.title || ""}
                              loading="lazy"
                              width={0}
                              height={0}
                              sizes="100vw"
                            />
                            <div className="block-description ps-3">
                              <h6 className="iq-title fw-500 mb-0">{video.title}</h6>
                              <span className="fs-12">{video.duration}</span>
                            </div>
                          </div>
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                  <div className="slider-prev swiper-button">
                    <i className="iconly-Arrow-Left-2 icli"></i>
                  </div>
                  <div className="slider-next swiper-button">
                    <i className="iconly-Arrow-Right-2 icli"></i>
                  </div>
                </div>
              </div>
            </div>
            <div className="slider-images" data-swiper="slider-images-ott">
              <Swiper
                key={String(themeSchemeDirection)}
                dir={String(themeSchemeDirection)}
                tag="ul"
                onSwiper={setThumbsSwiper}
                slidesPerView={1}
                watchSlidesProgress={true}
                allowTouchMove={false}
                loop={true}
                modules={[Thumbs]}
                className="swiper-container"
              >
                {videos.map((video, index) => (
                  <SwiperSlide className="p-0" key={video._id}>
                    <div className="slider--image block-images">
                      <Image
                        src={
                          video.thumbnailLandscape?.asset?._ref
                            ? getSanityImageUrl(video.thumbnailLandscape.asset._ref, 1920, 1080)
                            : video.thumbnailPortrait?.asset?._ref
                              ? getSanityImageUrl(video.thumbnailPortrait.asset._ref, 522, 760)
                              : `/assets/images/movies/ott${index + 1}.webp`
                        }
                        loading="lazy"
                        alt={video.title || "banner"}
                        width={0}
                        height={0}
                        sizes="100vw"
                      />
                    </div>
                    <div className="description">
                      <div className="row align-items-center h-100">
                        <div className="col-lg-6 col-md-12">
                          <div className="slider-content">
                            <div className="d-flex align-items-center RightAnimate mb-3">
                              {/* <span className="badge rounded-0 text-dark text-uppercase px-3 py-2 me-3 bg-white mr-3">
                                {video.ageRating || "PG"}
                              </span> */}
                              {/* Debug: Log categories for each video */}
                              {(() => {
                                if (!video.categories) {
                                  console.warn(`No categories for video:`, video);
                                } else {
                                  console.debug(`Categories for video '${video.title}':`, video.categories);
                                }
                                return null;
                              })()}
                              <ul className="p-0 mb-0 list-inline d-flex flex-wrap align-items-center movie-tag">
                                {video.categories && video.categories.map((category) => (
                                  <li key={category._id} className="position-relative text-capitalize font-size-14 letter-spacing-1">
                                    <Link
                                      href={`/categories/${category._id}`}
                                      className="text-decoration-none"
                                    >
                                      {category.title}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <h1 className="texture-text big-font letter-spacing-1 line-count-1 text-capitalize RightAnimate-two">
                              {video.title}
                            </h1>
                            <div className="line-count-3 RightAnimate-two">
                              {typeof video.description === 'string' ? (
                                <p>{video.description}</p>
                              ) : (
                                <PortableText value={video.description} />
                              )}
                            </div>
                            <div className="d-flex flex-wrap align-items-center gap-3 RightAnimate-three">
                              {video.rating && (
                                <div className="slider-ratting d-flex align-items-center">
                                  <ul className="ratting-start p-0 m-0 list-inline text-warning d-flex align-items-center justify-content-left">
                                    <li>
                                      <i
                                        className="fa fa-star"
                                        aria-hidden="true"
                                      ></i>
                                    </li>
                                  </ul>
                                  <span className="text-white ms-2 font-size-14 fw-500">
                                    {video.rating}
                                  </span>
                                  <span className="ms-2">
                                    <Image
                                      src="/assets/images/movies/imdb-logo.svg"
                                      alt="imdb logo"
                                      className="img-fluid"
                                      width={50}
                                      height={50}
                                      sizes="100vw"
                                    />
                                  </span>
                                </div>
                              )}
                              <span className="font-size-14 fw-500">
                                {video.duration}
                              </span>
                              {video.cast && video.cast.length > 0 && (
                                <div className="text-primary font-size-14 fw-500 text-capitalize">
                                  Starting:{" "}
                                  {video.cast.map((actor, idx) => (
                                    <React.Fragment key={idx}>
                                      {idx > 0 && <span className="text-body">,</span>}
                                      <Link
                                        href="/cast/detail"
                                        className="text-decoration-none ms-1"
                                      >
                                        {actor}
                                      </Link>
                                    </React.Fragment>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          <CustomButton
                            buttonTitle="Stream Now "
                            link={`/videos/${video.slug.current}`}
                            linkButton="false"
                          />
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};
export default OttHeroSlider;
